import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

interface QAPair {
  question: string;
  answer: string;
}

interface AttributionPanelProps {
  open: boolean;
  onClose: () => void;
  sectionLabel: string;
  contributingQA: QAPair[];
  accentColor: string;
}

export function AttributionPanel({
  open,
  onClose,
  sectionLabel,
  contributingQA,
  accentColor,
}: AttributionPanelProps) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          onClick={onClose}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/25 backdrop-blur-[2px]" />

          {/* Panel */}
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: "spring", damping: 30, stiffness: 360 }}
            className="relative w-full max-w-md bg-[#f7f3ec] rounded-t-3xl sm:rounded-2xl shadow-2xl border-t border-stone-200 sm:border sm:mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle — mobile only */}
            <div className="w-10 h-1 rounded-full bg-stone-300 mx-auto mt-3 mb-1 sm:hidden" />

            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-4 border-b border-stone-200"
              style={{ borderLeftWidth: 3, borderLeftColor: accentColor }}
            >
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-stone-400 mb-0.5">
                  How this was written
                </p>
                <p
                  className="text-[13px] font-semibold text-stone-700"
                >
                  {sectionLabel}
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-7 h-7 rounded-full flex items-center justify-center text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Q&A list */}
            <div className="px-5 py-4 space-y-4 max-h-[55vh] overflow-y-auto pb-8">
              {contributingQA.length === 0 ? (
                <p className="text-sm text-stone-400 text-center py-4">
                  No source answers recorded for this section.
                </p>
              ) : (
                contributingQA.map((qa, i) => (
                  <div key={i} className="space-y-1.5">
                    {/* Question */}
                    <p className="text-[11px] text-stone-400 leading-relaxed">
                      {qa.question}
                    </p>
                    {/* Answer */}
                    <div
                      className="rounded-lg px-3.5 py-2.5"
                      style={{ background: accentColor + "12" }}
                    >
                      <p
                        className="text-[14px] leading-relaxed text-stone-700"
                        style={{ fontFamily: "'Kalam', cursive", fontWeight: 400 }}
                      >
                        {qa.answer || (
                          <span className="text-stone-400">No answer recorded</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
